import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Select, SelectProps } from 'antd';
import styled from 'styled-components';
import { colorBorderSecondary } from '@atlas-frontend/design-tokens';
import {
  categoriesToHierarchy,
  CategoryHierarchyNode,
  CategoryReadDto,
  getMapBy,
  mapParentCodeToParentKey,
  objectWithRandomUniqueKey,
  useAllClassifierCategoriesQuery,
} from '../../index';
import { CustomColumnedCategorySelectOption } from './CustomColumnedCategorySelectOption';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './CustomColumnedCategorySelect.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'custom-columned-category-select'
);
addTranslations(translations);

const BUSINESS_AREA_CODE = 'BUSINESS_AREA' as const;
const DROPDOWN_VIEWPORT_OFFSET = 8; // px

export interface CustomColumnedCategorySelectProps extends SelectProps {
  id: string;
  value?: string[];
  onChange?: (value: string[]) => void;
}

export const CustomColumnedCategorySelect: FC<
  CustomColumnedCategorySelectProps
> = (props) => {
  const { id, value = [], onChange, ...rest } = props;
  const { t } = useTranslation();

  const [columns, setColumns] = useState<
    [string | null, CategoryHierarchyNode[]][]
  >([]);

  const [displayNamesByCode, setDisplayNamesByCode] = useState(
    new Map<string, CategoryReadDto>()
  );

  const { isFetching, data: categories } = useAllClassifierCategoriesQuery({
    classifierCode: BUSINESS_AREA_CODE,
  });

  useEffect(() => {
    if (categories) {
      setColumns([
        [
          null,
          categoriesToHierarchy(
            mapParentCodeToParentKey(
              categories.map(objectWithRandomUniqueKey)
            )[0]
          ),
        ],
      ]);
      setDisplayNamesByCode(getMapBy(categories, 'code'));
    }
  }, [categories]);

  const displayValue = useMemo(
    () =>
      displayNamesByCode.size === 0
        ? []
        : value.map((v) => {
            const { code, name } = displayNamesByCode.get(v) || {};
            return `${code} - ${name}`;
          }),
    [value, displayNamesByCode]
  );

  const addValue = useCallback(
    (...codes: string[]) => {
      onChange?.(Array.from(new Set([...value, ...codes])));
    },
    [onChange, value]
  );

  const removeValue = useCallback(
    (code: string) => {
      onChange?.(value.filter((v) => v !== code));
    },
    [onChange, value]
  );

  const getDropdownElement = useCallback(
    () => document.querySelector<HTMLDivElement>(`.${id}-dropdown`),
    [id]
  );
  const getInputElement = useCallback(
    () => document.getElementById(id)?.closest<HTMLDivElement>('.ant-select'),
    [id]
  );

  const timeoutRef = useRef<number | undefined>(undefined);
  const containDropdownInViewPortByMaxWidth = useCallback(
    (isDropdownVisible?: boolean) => {
      window.clearTimeout(timeoutRef.current);

      if (isDropdownVisible === false) {
        return;
      }

      timeoutRef.current = window.setTimeout(() => {
        const dropdown = getDropdownElement();
        const input = getInputElement();

        if (dropdown && input) {
          const dropdownRect = dropdown.getBoundingClientRect();
          const inputRect = input.getBoundingClientRect();

          dropdown.style.maxWidth =
            dropdownRect.left === inputRect.left
              ? `${
                  window.innerWidth - inputRect.left - DROPDOWN_VIEWPORT_OFFSET
                }px`
              : dropdownRect.right === inputRect.right
              ? `${inputRect.right - DROPDOWN_VIEWPORT_OFFSET}px`
              : '';
        }
      });
    },
    [getDropdownElement, getInputElement]
  );

  useEffect(
    () => () => {
      window.clearTimeout(timeoutRef.current);
    },
    []
  );

  const scrollToEnd = useCallback(() => {
    const dropdown = getDropdownElement();
    if (dropdown) {
      window.setTimeout(() => {
        dropdown.scrollLeft = dropdown.scrollWidth;
      });
    }
  }, [getDropdownElement]);

  return (
    <StyledSelect
      mode="multiple"
      allowClear
      id={id}
      classNames={{
        popup: { root: `${id}-dropdown` },
      }}
      value={displayValue}
      loading={isFetching}
      disabled={isFetching}
      placeholder={isFetching ? t('loading-categories') : undefined}
      styles={{
        popup: { root: { padding: 0, width: 'max-content', overflow: 'auto' } },
      }}
      onDeselect={(displayLabel) => {
        removeValue(displayLabel.split(' - ')[0]);
      }}
      onClear={() => {
        onChange?.([]);
      }}
      onDropdownVisibleChange={containDropdownInViewPortByMaxWidth}
      popupRender={() => (
        <Flex>
          {columns.map(([columnParentCode, categories], columnIndex) => (
            <ColumnWrapper key={columnParentCode}>
              <Flex vertical>
                {categories.map((category) => (
                  <CustomColumnedCategorySelectOption
                    key={category.code}
                    category={category}
                    columns={columns}
                    value={value}
                    onClick={(isOpen, hasChildren) => {
                      if (isOpen) {
                        scrollToEnd();
                      } else if (hasChildren) {
                        setColumns(
                          columns
                            .slice(0, columnIndex + 1)
                            .concat([[category.code, category.children]])
                        );
                        containDropdownInViewPortByMaxWidth();
                        scrollToEnd();
                      }
                    }}
                    onCheckboxChange={(checked) =>
                      checked
                        ? addValue(...getAllDescendantCodes(category))
                        : removeValue(category.code)
                    }
                  />
                ))}
              </Flex>
            </ColumnWrapper>
          ))}
        </Flex>
      )}
      {...rest}
    />
  );
};

const getAllDescendantCodes = (
  category: CategoryHierarchyNode
): Set<string> => {
  const codes = new Set<string>([category.code]);

  for (const child of category.children) {
    for (const descendantCode of getAllDescendantCodes(child)) {
      codes.add(descendantCode);
    }
  }

  return codes;
};

const StyledSelect = styled(Select<string[]>)`
  .ant-select-selector {
    max-height: 300px;
    overflow: auto;
  }
`;

const ColumnWrapper = styled.div`
  overflow: auto;
  max-height: 400px;
  padding: 4px;
  min-width: 300px;
  max-width: 400px;

  & + & {
    border-left: 1px solid ${colorBorderSecondary};
  }
`;
